import { http } from '@/app/lib/http/client'

export interface WbsProduct {
  carrier_id: string
  description: string
  group: string
  amount: string
  note: string
  order: number
  check_amount: boolean
}

export interface WbsProductsResponse {
  data: {
    data: WbsProduct[]
    total: number
    per_page: number
    current_page: number
    last_page: number
  }
  message: string
}

export interface WbsBalanceData {
  Confirmation: string
  Saldo_Inicial: string
  Compras: string
  Ventas: string
  Comision: string
  Balance: string
}

export interface WbsBalanceResponse {
  data: WbsBalanceData
  message: string
}

export interface WbsCarrierUsage {
  carrier_id: string
  count: number
}

export interface WbsCheckoutConfig {
  service_commission_amount: string
}

export interface WbsDebtData {
  response: number
  editable: boolean
  rcode: number
  amount: string
  message: string
}

export type WbsTransactionStatus =
  | 'CREATED'
  | 'SENDING'
  | 'PENDING'
  | 'CONSULTING'
  | 'SUCCESS'
  | 'FAILED'
  | 'EXPIRED'
  | 'COMMUNICATION_ERROR'
  | 'PENDING_RESOLUTION'

export interface WbsTransaction {
  id: number
  folio_pos: string
  sale_id: number | null
  type: 'RECHARGE' | 'SERVICE_PAYMENT'
  carrier_id: string
  carrier_description: string
  amount: string
  commission_amount: string
  reference: string
  status: WbsTransactionStatus
  wbs_confirmation?: string | null
  wbs_description?: string | null
  wbs_folio?: string | null
  created_at: string
  updated_at?: string | null
}

export interface WbsTransactionStatusResponse {
  data: { transaction: WbsTransaction }
  message: string
}

export function wbsSaleAmounts(sale: any): { subtotal: number; tax: number; total: number } {
  return {
    subtotal: Number(sale?.amounts?.subtotal ?? sale?.subtotal_amount ?? 0),
    tax: Number(sale?.amounts?.tax_total ?? sale?.tax_total_amount ?? 0),
    total: Number(sale?.amounts?.total ?? sale?.total_amount ?? 0),
  }
}

export const wbsService = {
  testConnection: async (credentials?: any) => {
    const response = await http.post('/integrations/wbs/test-connection', credentials)
    return response.data
  },

  getBalance: async (): Promise<WbsBalanceResponse> => {
    const response = await http.get('/integrations/wbs/balance')
    return response.data
  },

  getCheckoutConfig: async (): Promise<WbsCheckoutConfig> => {
    const response = await http.get('/integrations/wbs/checkout-config')
    return response.data?.data
  },

  getProducts: async (params?: { search?: string; page?: number; per_page?: number }): Promise<WbsProductsResponse> => {
    const response = await http.get('/integrations/wbs/products', { params })
    return response.data
  },

  getAllProducts: async (): Promise<WbsProduct[]> => {
    const response = await http.get('/integrations/wbs/products/all')
    return response.data?.data ?? []
  },

  initiateTransaction: async (data: {
    type: 'RECHARGE' | 'SERVICE_PAYMENT',
    carrier_id: string,
    carrier_description?: string,
    amount: string,
    reference: string,
    payment_method: string
  }) => {
    const response = await http.post('/integrations/wbs/transactions', data)
    return response.data
  },

  checkStatus: async (folio: string): Promise<WbsTransactionStatusResponse> => {
    const response = await http.get(`/integrations/wbs/transactions/${folio}/status`)
    return response.data
  },

  checkDebt: async (data: { carrier_id: string, reference: string }): Promise<{ data: WbsDebtData; message: string }> => {
    const response = await http.post('/integrations/wbs/debt', data)
    return response.data
  },

  getTransactions: async (params?: { search?: string; page?: number; per_page?: number }) => {
    const response = await http.get('/integrations/wbs/transactions', { params })
    return response.data
  },

  checkoutTransaction: async (folio: string, data: { payment_method_id: number; cash_received_amount?: string }) => {
    const response = await http.post(`/integrations/wbs/transactions/${folio}/sale`, data)
    return response.data
  },

  /**
   * Devuelve el ranking de operadores por número de transacciones exitosas
   * en una sucursal específica, ordenado de mayor a menor uso.
   */
  getCarrierUsage: async (): Promise<WbsCarrierUsage[]> => {
    const response = await http.get('/integrations/wbs/carriers/usage')
    return response.data?.data ?? []
  },
}
